import { useState } from "react";
import type { InvoiceDto } from "@grizzly/shared";
import { invoiceStatus } from "@grizzly/shared";
import { formatAmount } from "../lib/format.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { InvoiceDialog } from "../components/finance/invoiceDialog.js";
import { PaymentDialog } from "../components/finance/paymentDialog.js";
import { ExpenseDialog } from "../components/finance/expenseDialog.js";
import { InvoicePanel } from "../components/finance/invoicePanel.js";
import { CashLedger } from "../components/finance/cashLedger.js";
import { useFinanceData } from "../lib/useFinanceData.js";
import { useI18n, type MessageKey } from "../i18n/index.js";

export function InvoicesPage() {
  const { t } = useI18n();
  const { invoices, payments, categories, customers, cash, error, reload } =
    useFinanceData();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);

  const invoiceColumns: Column<InvoiceDto>[] = [
    {
      key: "number",
      header: t("common.fields.invoice"),
      render: (r) => (
        <button
          type="button"
          onClick={() => setSelectedId(r.id)}
          className="tabular font-medium text-action-primary-text hover:underline"
        >
          {r.number}
        </button>
      ),
    },
    {
      key: "customer",
      header: t("common.fields.customer"),
      render: (r) => r.customerName,
    },
    {
      key: "status",
      header: t("common.fields.status"),
      render: (r) => (
        <Badge tone={invoiceStatus(r.overdue ? "overdue" : r.status).tone}>
          {r.overdue
            ? t("status.invoice.overdue")
            : t(`status.invoice.${r.status}` as MessageKey)}
        </Badge>
      ),
    },
    {
      key: "total",
      header: t("common.fields.total"),
      render: (r) => <span className="tabular">{formatAmount(r.totalMinor)}</span>,
    },
    {
      key: "balance",
      header: t("common.fields.balance"),
      render: (r) => <span className="tabular">{formatAmount(r.balanceMinor)}</span>,
    },
    { key: "due", header: t("common.fields.due"), render: (r) => r.dueDate ?? "-" },
  ];

  const selected = invoices.find((i) => i.id === selectedId) ?? null;

  return (
    <>
      <PageHeader
        title={t("nav.invoices")}
        description={t("finance.description")}
        actions={
          <>
            <Button variant="secondary" onClick={() => setExpenseOpen(true)}>
              {t("finance.recordExpense")}
            </Button>
            <Button variant="secondary" onClick={() => setPayOpen(true)}>
              {t("finance.recordPayment")}
            </Button>
            <Button variant="primary" onClick={() => setInvoiceOpen(true)}>
              {t("finance.newInvoice")}
            </Button>
          </>
        }
      />

      {error ? (
        <p role="alert" className="mb-4 text-sm text-action-danger">
          {error}
        </p>
      ) : null}

      <CashLedger summary={cash} />

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <section>
            <h2 className="mb-2 text-sm font-semibold text-ink">
              {t("finance.invoices")}
            </h2>
            <DataTable
              caption={t("finance.invoices")}
              rows={invoices}
              rowKey={(r) => r.id}
              columns={invoiceColumns}
              emptyTitle={t("finance.noInvoices")}
              emptyDescription={t("finance.noInvoicesDesc")}
            />
          </section>
          <section>
            <h2 className="mb-2 text-sm font-semibold text-ink">
              {t("finance.payments")}
            </h2>
            <DataTable
              caption={t("finance.payments")}
              rows={payments}
              rowKey={(r) => r.id}
              columns={[
                {
                  key: "amount",
                  header: t("common.fields.amount"),
                  render: (r) => (
                    <span className="tabular">{formatAmount(r.amountMinor)}</span>
                  ),
                },
                {
                  key: "customer",
                  header: t("common.fields.customer"),
                  render: (r) => r.customerName,
                },
                {
                  key: "invoice",
                  header: t("common.fields.invoice"),
                  render: (r) => r.invoiceNumber ?? t("finance.unapplied"),
                },
                {
                  key: "method",
                  header: t("common.fields.method"),
                  render: (r) => r.method ?? "-",
                },
              ]}
              emptyTitle={t("finance.noPayments")}
              emptyDescription={t("finance.noPaymentsDesc")}
            />
          </section>
        </div>

        {selected ? (
          <Card>
            <InvoicePanel invoice={selected} onChanged={reload} />
          </Card>
        ) : (
          <Card>
            <p className="text-sm text-ink-muted">{t("finance.selectInvoice")}</p>
          </Card>
        )}
      </div>

      <InvoiceDialog
        open={invoiceOpen}
        onOpenChange={setInvoiceOpen}
        customers={customers}
        onCreated={reload}
      />
      <PaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        customers={customers}
        invoices={invoices}
        onRecorded={reload}
      />
      <ExpenseDialog
        open={expenseOpen}
        onOpenChange={setExpenseOpen}
        categories={categories}
        onRecorded={reload}
      />
    </>
  );
}
